import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, ArrowLeft, Shield, Smartphone, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const PROJECT_LOGO = "/assets/favicon-icon.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register, verifyMfa, mfaPending, sendEmailOtp } = useAuth();
  
  const [activeTab, setActiveTab] = useState('signin');
  const [isLoading, setIsLoading] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [testOtp, setTestOtp] = useState('');
  
  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await login(loginForm.email, loginForm.password);
      
      if (result.requiresMfa) {
        setShowMfa(true);
        if (result.mfaType === 'email') {
          toast.info('A verification code has been sent to your email');
        } else {
          toast.info('Enter the code from your authenticator app');
        }
      } else if (result.mustChangePassword) {
        toast.info('You must set a new password');
        // Use window.location to ensure navigation happens before React re-renders
        window.location.href = '/change-password';
        return;
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await register(
        registerForm.email,
        registerForm.password,
        registerForm.firstName,
        registerForm.lastName
      );
      toast.success('Account created! Please sign in.');
      setActiveTab('signin');
      setLoginForm({ email: registerForm.email, password: '' });
      setRegisterForm({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (mfaCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await verifyMfa(mfaPending?.email || loginForm.email, mfaCode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const result = await sendEmailOtp(mfaPending?.email || loginForm.email);
      if (result.otp_for_testing) {
        setTestOtp(result.otp_for_testing);
      }
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Check your email for a temporary password');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div data-testid="forgot-password-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
          <div className="relative z-10 text-center space-y-8">
            <img src={PROJECT_LOGO} alt="MySpecWorkspace" className="w-48 h-48 object-contain mx-auto drop-shadow-2xl" />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Password Recovery</h2>
            <p className="text-slate-400 max-w-sm">We'll send a temporary password to your registered email</p>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md space-y-8">
            <Button variant="ghost" onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }} className="mb-4" data-testid="back-to-login-from-forgot">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
            </Button>
            <Card className="border-slate-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-4">
                  <Mail className="w-8 h-8 text-violet-600" />
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {forgotSent ? 'Check Your Email' : 'Forgot Password'}
                </CardTitle>
                <CardDescription>
                  {forgotSent
                    ? 'A temporary password has been sent to your email. Use it to log in, then set a new password.'
                    : "Enter your email address and we'll send you a temporary password"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 pt-4">
                {forgotSent ? (
                  <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => { setShowForgotPassword(false); setForgotSent(false); setLoginForm({ ...loginForm, email: forgotEmail }); }} data-testid="back-to-signin-btn">
                    Back to Sign In
                  </Button>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="forgotEmail">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input id="forgotEmail" type="email" placeholder="you@company.com"
                          value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                          className="pl-10" required data-testid="forgot-email-input" />
                      </div>
                    </div>
                    <Button type="submit" disabled={isLoading} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="forgot-submit-btn">
                      {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>) : 'Send Temporary Password'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (showMfa) {
    return (
      <div 
        data-testid="mfa-page"
        className="min-h-screen grid grid-cols-1 lg:grid-cols-2"
      >
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-slate-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 text-center space-y-8">
            <img 
              src={PROJECT_LOGO}
              alt="MySpecWorkspace"
              className="w-48 h-48 object-contain mx-auto drop-shadow-2xl"
            />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Secure Access
            </h2>
            <p className="text-slate-400 max-w-sm">
              Multi-factor authentication protects your technical documentation data
            </p>
          </div>
        </div>

        {/* Right Panel - MFA Form */}
        <div className="flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md space-y-8">
            <Button
              variant="ghost"
              onClick={() => {
                setShowMfa(false);
                setMfaCode('');
              }}
              className="mb-4"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>

            <Card className="border-slate-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-4">
                  {mfaPending?.mfa_type === 'totp' ? (
                    <Smartphone className="w-8 h-8 text-violet-600" />
                  ) : (
                    <Shield className="w-8 h-8 text-violet-600" />
                  )}
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Verification Required
                </CardTitle>
                <CardDescription className="text-slate-500">
                  {mfaPending?.mfa_type === 'totp' 
                    ? 'Enter the code from your authenticator app'
                    : 'Enter the code sent to your email'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                <div className="flex justify-center">
                  <InputOTP
                    value={mfaCode}
                    onChange={setMfaCode}
                    maxLength={6}
                    data-testid="mfa-code-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {testOtp && (
                  <p className="text-center text-sm text-slate-500 bg-slate-50 p-2 rounded">
                    Test OTP: <span className="font-mono font-bold text-violet-600">{testOtp}</span>
                  </p>
                )}

                <Button
                  onClick={handleMfaVerify}
                  disabled={isLoading || mfaCode.length !== 6}
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  data-testid="verify-mfa-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>

                {mfaPending?.mfa_type === 'email' && (
                  <Button
                    variant="ghost"
                    onClick={handleResendOtp}
                    className="w-full text-slate-600"
                    data-testid="resend-otp-btn"
                  >
                    Didn't receive a code? Resend
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="login-page"
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2"
    >
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 text-center space-y-8">
          <img 
            src={PROJECT_LOGO}
            alt="MySpecWorkspace"
            className="w-56 h-56 object-contain mx-auto drop-shadow-2xl"
          />
          <div className="space-y-4">
            <h2 className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MySpecWorkspace
            </h2>
            <p className="text-slate-400 max-w-md text-lg">
              All your specs. One workspace.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login/Register Forms */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src={PROJECT_LOGO}
              alt="MySpecWorkspace"
              className="w-24 h-24 object-contain mx-auto"
            />
            <h1 className="text-2xl font-bold mt-4 text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MySpecWorkspace
            </h1>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-slate-200">
              <TabsTrigger 
                value="signin" 
                data-testid="signin-tab"
                className="data-[state=active]:bg-white"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                data-testid="signup-tab"
                className="data-[state=active]:bg-white"
              >
                Create Account
              </TabsTrigger>
            </TabsList>

            {/* Sign In Tab */}
            <TabsContent value="signin">
              <Card className="border-slate-200 shadow-xl">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Welcome Back
                  </CardTitle>
                  <CardDescription>
                    Sign in to access your projects and tools
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@company.com"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                          className="pl-10"
                          required
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="password"
                          type={showLoginPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                          className="pl-10 pr-10"
                          required
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid="login-password-toggle"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => { setShowForgotPassword(true); setForgotEmail(loginForm.email); }}
                        className="text-sm text-violet-600 hover:text-violet-700 hover:underline transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sign Up Tab */}
            <TabsContent value="signup">
              <Card className="border-slate-200 shadow-xl">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Create Account
                  </CardTitle>
                  <CardDescription>
                    Register to start managing your S1000D projects
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="firstName"
                            type="text"
                            placeholder="John"
                            value={registerForm.firstName}
                            onChange={(e) => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                            className="pl-10"
                            required
                            data-testid="register-firstname-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Doe"
                          value={registerForm.lastName}
                          onChange={(e) => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                          required
                          data-testid="register-lastname-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="registerEmail">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="registerEmail"
                          type="email"
                          placeholder="you@company.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          className="pl-10"
                          required
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="registerPassword">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="registerPassword"
                          type={showRegPassword ? "text" : "password"}
                          placeholder="Min 6 characters"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          className="pl-10 pr-10"
                          required
                          data-testid="register-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegPassword(!showRegPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid="register-password-toggle"
                          tabIndex={-1}
                        >
                          {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type={showRegConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                          className="pl-10 pr-10"
                          required
                          data-testid="register-confirm-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          data-testid="register-confirm-password-toggle"
                          tabIndex={-1}
                        >
                          {showRegConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
